// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import React, { useContext, createContext, useState, useEffect } from 'react'
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser'
import { InteractionType } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { findIana } from 'windows-iana'

import config from './config'
import {
  getUserCalendars,
  getUserWeekCalendar,
  getEventDetailsById,
  createEvent,
  searchEventsWithDetails
} from 'utils/api/GraphService'
import dayjs from 'dayjs'
import {
  cacheOtherCalendarsEvents,
  createGeneralGroupIfNeeded
} from '../CalendarSyncProdivder'
import { MSCalendar } from 'types/ms_outlook'
import { outlookPrefix } from 'utils/constants'
import Logger from 'services/Logger'
import { Event, PatternedRecurrence } from '@microsoft/microsoft-graph-types'
import {
  DocType,
  EventDoc,
  EventRecurrence,
  EventReminder,
  EventStatus,
  RECURRENCE_UNIT,
  TaskType,
  UserSettingsDoc
} from 'types/api/changes'
import { allOutlookCalendars } from 'services/db/Queries/GroupQueries'
import Database from 'services/db/Database'
import { otherCalendarEvents } from 'services/db/Queries/Event'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { darken } from '@mui/material'
import { ConferenceData, ICalendar } from 'types/google_events'
import {
  convertRRuleToGroupcalRecurrence,
  CurrentConvertedCalendars
} from 'utils/google_events'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import { DayOfWeek } from 'microsoft-graph'
import { getDefaultTimezone, msalInstance } from 'index'
import { RRule } from 'rrule'
import { removePrefix } from 'utils/object'
import { cacheConvertedCalendarsToLocalStorage } from 'utils/third_party_calendars'

// <AppContextSnippet>
export interface AppUser {
  displayName?: string
  email?: string
  avatar?: string
  timeZone?: string
  timeFormat?: string
}

export interface AppError {
  message: string
  debug?: string
}

export type OutlookContext = {
  user?: AppUser
  error?: AppError
  signIn?: Function
  signOut?: Function
  displayError?: Function
  clearError?: Function
  getAuthProvider?: (
    email: string
  ) => AuthCodeMSALBrowserAuthenticationProvider | undefined
  syncCalendars?: (
    start?: string,
    end?: string,
    withUnarchiving?: boolean,
    query?: string
  ) => Promise<void>
  getEventDetails?: Function
  addEvent?: Function
  verifyCalendarConnection?: Function
}

const outlookConext = createContext<OutlookContext>({
  user: undefined,
  error: undefined,
  signIn: undefined,
  signOut: undefined,
  displayError: undefined,
  clearError: undefined,
  getAuthProvider: undefined,
  syncCalendars: undefined,
  getEventDetails: undefined,
  addEvent: undefined,
  verifyCalendarConnection: undefined
})

export function useOutlookContext(): OutlookContext {
  return useContext(outlookConext)
}

interface ProvideOutlookContextProps {
  children: React.ReactNode
}

export const outlookProviders = new Map<
  string,
  AuthCodeMSALBrowserAuthenticationProvider
>()

export function getAuthProvider(
  email?: string
): AuthCodeMSALBrowserAuthenticationProvider | undefined {
  if (!email) return undefined

  for (const account of msalInstance.getAllAccounts()) {
    const email = account.username || account.localAccountId
    if (!email) continue

    try {
      const provider = new AuthCodeMSALBrowserAuthenticationProvider(
        msalInstance,
        {
          account: account,
          scopes: config.scopes,
          interactionType: InteractionType.Popup
        }
      )
      outlookProviders.set(email, provider)
      Logger.blue(
        `Outlook: Created auth provider for account`,
        email,
        outlookProviders
      )
    } catch (err) {
      Logger.red(`Outlook: Failed to create auth provider for`, email, err)
    }
  }
  return outlookProviders.get(email)
}

export default function ProvideOutlookContext({
  children
}: ProvideOutlookContextProps) {
  const auth = useProvideOutlookContext()
  return (
    <outlookConext.Provider value={auth}>{children}</outlookConext.Provider>
  )
}
// </AppContextSnippet>

function useProvideOutlookContext() {
  const msal = useMsal()
  const [user, setUser] = useState<AppUser | undefined>(undefined)
  const [error, setError] = useState<AppError | undefined>(undefined)

  function markCalendarsAsProblematic() {
    allOutlookCalendars().then((calendars) => {
      Logger.blue('Outlook: all outlook calendars', calendars)
      if (calendars.length > 0) {
        calendars.forEach((calendar) => {
          if (
            calendar.SyncProblem !== '1' &&
            calendar.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)
          ) {
            Database.write(async () => {
              calendar.update((local) => {
                local.SyncProblem = '1'
              })
            })
          }
        })
      }
    })
  }

  async function verifyCalendarConnection() {
    Logger.blue('Outlook: MS accounts', msal.accounts)

    if (msal.accounts.length === 0) {
      Logger.red('No MS accounts')
      markCalendarsAsProblematic()
      return
    }

    const calendars = await allOutlookCalendars()
    const currentUserId = LocalStorage.get(LocalStorageKey.USER_ID)

    for (const calendar of calendars) {
      const correspondingMSALAccount = msalInstance
        .getAllAccounts()
        .find(
          (account) =>
            account.username === removePrefix(calendar.CalendarEmail ?? '')
        )

      Logger.blue(
        'Outlook: Verifying calendar',
        calendar.CalendarEmail,
        correspondingMSALAccount
      )

      const isOwnedByCurrentUser = calendar.OwnerID === currentUserId
      const hasNoSyncProblem = calendar.SyncProblem !== '1'

      if (hasNoSyncProblem && isOwnedByCurrentUser) {
        if (!correspondingMSALAccount) {
          await markCalendarAsProblematic(calendar)
        } else {
          await verifyTokenValidity(calendar, correspondingMSALAccount)
        }
      }
    }
  }

  async function markCalendarAsProblematic(calendar: any) {
    await Database.write(async () => {
      calendar.update((local: any) => {
        local.SyncProblem = '1'
      })
    })
  }

  async function verifyTokenValidity(calendar: any, account: any) {
    const silentRequest = {
      scopes: ['calendars.readwrite'],
      account
    }

    try {
      await msal.instance.initialize()
      const response = await msal.instance.acquireTokenSilent(silentRequest)

      if (response.expiresOn && response.expiresOn < new Date()) {
        await markCalendarAsProblematic(calendar)
      }
    } catch (err) {
      console.error('Error acquiring token', err)
      await markCalendarAsProblematic(calendar)
    }
  }

  useEffect(() => {
    verifyCalendarConnection()
  }, [msal.accounts])

  const displayError = (message: string, debug?: string) => {
    setError({ message, debug })
  }

  const clearError = () => {
    setError(undefined)
  }

  // <AuthProviderSnippet>
  // Used by the Graph SDK to authenticate API calls
  // Providers are now retrieved by email using getAuthProvider(email)
  // </AuthProviderSnippet>

  // <SignInSnippet>
  const signIn = async (onCancel?: () => void) => {
    try {
      const res = await msal.instance.loginPopup({
        scopes: config.scopes,
        prompt: 'select_account'
      })

      Logger.blue('Outlook: SignIn', res)

      const start = LocalStorage.get(LocalStorageKey.VISIBLE_FROM) as string
      const end = LocalStorage.get(LocalStorageKey.VISIBLE_TO) as string

      await syncCalendars(start, end, true)
    } catch (e) {
      Logger.red('Outlook: SignIn error', e, onCancel)
      if (onCancel) onCancel()
    }
  }
  // </SignInSnippet>

  // <SignOutSnippet>
  const signOut = async (email: string) => {
    await msal.instance.logoutPopup()
    setUser(undefined)
  }
  // </SignOutSnippet>

  const syncCalendars = async (
    start?: string,
    end?: string,
    withUnarchiving?: boolean,
    query?: string
  ) => {
    Logger.blue(
      'ThirdPartySync:Outlook started for accs',
      msalInstance.getAllAccounts()
    )

    for (const account of msalInstance.getAllAccounts()) {
      const email = account.username || account.localAccountId
      try {
        Logger.blue('Outlook:SyncCalendars:AccountEmail', email)
        const accAuthProvider = getAuthProvider(email)

        if (!accAuthProvider) {
          Logger.red(
            'Outlook:SyncCalendars: No auth provider for account',
            email
          )
          continue
        }

        Logger.orange('Outlook:SyncCalendars:Account', account)
        const calendars = await getUserCalendars(accAuthProvider)
        Logger.orange('Outlook:SyncCalendars:Account:Calendars', calendars)

        let groupId: string | undefined = undefined
        calendars.forEach((calendar: MSCalendar) => {
          if (calendar.isDefaultCalendar) {
            groupId = outlookPrefix + calendar.owner?.address
          }
        })

        for (const calendar of calendars) {
          const readOnly = calendar.canEdit === false
          if (calendar.isDefaultCalendar) {
            const group = await createGeneralGroupIfNeeded(
              outlookPrefix,
              email,
              calendar.hexColor.length > 0 ? calendar.hexColor : '#3293eb',
              email,
              'Outlook Calendar',
              withUnarchiving
            )
            Logger.blue('Outlook:SyncCalendars:Group', group)

            groupId = group._id
          }

          if (
            CurrentConvertedCalendars.find(
              (calendarFromServer) => calendarFromServer.id === calendar.id
            ) !== undefined
          ) {
            // remove existing to update
            CurrentConvertedCalendars.splice(
              CurrentConvertedCalendars.findIndex(
                (calendarFromServer) => calendarFromServer.id === calendar.id
              ),
              1
            )
          }

          let iCalendar: ICalendar = {
            title: calendar.name
          }

          if (
            CurrentConvertedCalendars.find(
              (calendarFromServer) => calendarFromServer.id === calendar.id
            ) === undefined
          ) {
            iCalendar.id = calendar.id
            iCalendar.title = calendar.isDefaultCalendar ? email : calendar.name
            iCalendar.name = calendar.isDefaultCalendar ? email : calendar.name
            iCalendar.backgroundColor =
              calendar.hexColor.length > 0 ? calendar.hexColor : '#3293eb'
            iCalendar.accessRole = calendar.canEdit ? 'owner' : 'reader'
            iCalendar.prefix = outlookPrefix
            iCalendar.primary = calendar.isDefaultCalendar
            iCalendar.primaryCalendarEmail = email
            iCalendar.canEdit = calendar.canEdit
            iCalendar.localId = groupId
            iCalendar.syncable = true
            Logger.blue(
              'Outlook:SyncCalendars:Adding converted calendar',
              iCalendar
            )
            CurrentConvertedCalendars.push(iCalendar)
          } else {
            // keep link between group id and localid
            const existingCalendar = CurrentConvertedCalendars.find(
              (calendarFromServer) => calendarFromServer.id === calendar.id
            )
            if (existingCalendar) {
              existingCalendar.localId = groupId
            }
          }

          let us = await UserSettings()
          const groupSettings = us.GroupsSettings?.[groupId ?? '']

          if (
            groupSettings?.ConvertedCalendars?.find(
              (cal) => cal.id === calendar.id
            )?.syncable === false
          ) {
            Logger.blue(
              'PersonalCalendars:Calendar is marked as not syncable, skipping',
              calendar
            )
            continue
          }

          const startDateTime =
            start ?? LocalStorage.get(LocalStorageKey.VISIBLE_FROM)

          const endDateTime =
            end ?? LocalStorage.get(LocalStorageKey.VISIBLE_TO)
          if (!startDateTime || !endDateTime) {
            return
          }

          const eventsForCalendar =
            query === undefined
              ? await getUserWeekCalendar(
                  accAuthProvider,
                  getDefaultTimezone(),
                  calendar.isDefaultCalendar ? undefined : calendar.id,
                  startDateTime,
                  endDateTime,
                  false // Use beta API
                )
              : await searchEventsWithDetails(accAuthProvider, query)

          if (groupId)
            processEventsFromOutlook(
              eventsForCalendar,
              calendar,
              groupId,
              startDateTime,
              endDateTime,
              email,
              query,
              readOnly
            )
          else {
            Logger.red(
              'Outlook:ProcessEvents',
              'Default group for outlook not found'
            )
          }
        }
      } catch (err) {
        console.error('Error during sync events with Outlook', err)
        const allOutlookGroups = await allOutlookCalendars()
        console.error('All Outlook groups', allOutlookGroups)
        const specificGroup = allOutlookGroups.find((cal) =>
          cal.CalendarEmail?.includes(email ?? '')
        )
        console.error('Specific group for email', email, specificGroup)

        if (specificGroup === undefined) {
          console.error(
            'No specific group found, skipping marking as problematic'
          )

          continue
        }

        markCalendarAsProblematic(specificGroup)
      }
    }

    Logger.blue(
      'Outlook:SyncCalendars:Finished syncing calendars',
      CurrentConvertedCalendars
    )

    cacheConvertedCalendarsToLocalStorage()
  }

  const getEventDetails = async (eventId: string, email?: string) => {
    // Use provided email or default to first account
    const accountEmail =
      email || msal.accounts[0]?.username || msal.accounts[0]?.localAccountId

    if (!accountEmail) {
      Logger.red('Outlook: No account available for getEventDetails')
      return undefined
    }

    const authProvider = getAuthProvider(accountEmail)
    if (!authProvider) {
      Logger.red('Outlook: No auth provider for email', accountEmail)
      return undefined
    }

    return getEventDetailsById(authProvider, eventId)
  }

  async function addEvent(
    event: Event,
    calendarId: string,
    email?: string
  ): Promise<Event> {
    // Use provided email or default to first account
    const accountEmail =
      email || msal.accounts[0]?.username || msal.accounts[0]?.localAccountId

    if (!accountEmail) {
      throw new Error('Outlook: No account available for addEvent')
    }

    const authProvider = getAuthProvider(accountEmail)
    if (!authProvider) {
      throw new Error(`Outlook: No auth provider for email ${accountEmail}`)
    }

    Logger.blue('Outlook: Add event', event)
    return createEvent(authProvider, event, calendarId)
  }

  return {
    user,
    error,
    signIn,
    signOut,
    displayError,
    clearError,
    getAuthProvider,
    syncCalendars,
    getEventDetails,
    addEvent,
    verifyCalendarConnection
  }
}

async function processEventsFromOutlook(
  events: Event[],
  calendar: MSCalendar,
  groupId: string,
  startDateTime: string,
  endDateTime: string,
  email: string,
  query?: string,
  readOnly: boolean = false
) {
  const convertedOutlookEvents = events.map((event: Event) => {
    const tfmEvent: EventDoc = {
      ...outlookEventToTfmEvent(event, email),
      GroupID: groupId,
      ThirdPartyID: calendar.id,
      Color: darken(
        calendar.hexColor.length > 0 ? calendar.hexColor : '#3293eb',
        0.15
      )
    }

    if (readOnly) {
      tfmEvent.PersonalOwnerId = 'read-only'
    }

    return tfmEvent
  })

  Logger.blue('Outlook: Processed events', convertedOutlookEvents)

  const fromDateISO = dayjs(startDateTime).toISOString()
  const toDateISO = dayjs(endDateTime).subtract(1, 'day').toISOString()

  try {
    const existing = (
      await otherCalendarEvents(
        groupId,
        fromDateISO,
        toDateISO,
        query !== undefined
      )
    ).filter((event) => event.ThirdPartyID === calendar.id)

    cacheOtherCalendarsEvents(events, existing, convertedOutlookEvents, query)
  } catch (error) {
    Logger.red('Outlook: Error processing events', error)
  }
}

export function outlookEventToTfmEvent(event: Event, email: string): EventDoc {
  let reminders: EventReminder[] = []
  if (event.isReminderOn) {
    const reminder: EventReminder = {
      isOn: '1',
      isRelativeReminder: '1',
      offset: (event.reminderMinutesBeforeStart ?? 60) * 60,
      AlertTime: ''
    }
    reminders.push(reminder)
  }

  let tz = getDefaultTimezone()

  if (event.originalStartTimeZone && event.originalStartTimeZone !== 'UTC') {
    const result = findIana(event.originalStartTimeZone)

    if (result.length > 0) {
      tz = result[0]
    } else {
      tz = event.originalStartTimeZone
    }
  }

  let conferenceData: ConferenceData | undefined = undefined

  if (
    event.onlineMeeting &&
    (event.onlineMeetingUrl || event.isOnlineMeeting)
  ) {
    conferenceData = {
      entryPoints: [
        {
          entryPointType: 'video',
          uri: event.onlineMeetingUrl ?? event.onlineMeeting?.joinUrl ?? ''
        }
      ],
      conferenceSolution: {
        name: 'Microsoft Teams',
        iconUri:
          'https://s3.amazonaws.com/res.twentyfour.me/ImagesInTheApps/skype.svg'
      }
    }
  }

  let start = dayjs(event.start?.dateTime).unix()
  let end = dayjs(event.end?.dateTime).unix()

  if (event.isAllDay) {
    start = dayjs(event.start?.dateTime)
      .tz('UTC')
      .add(12, 'hour')
      .startOf('day')
      .unix()
    end = dayjs(event.end?.dateTime)
      .tz('UTC')

      .unix()
  }

  return {
    _id: (event as any).occurrenceId ? (event as any).occurrenceId : event.id,
    local_id: (event as any).occurrenceId
      ? (event as any).occurrenceId
      : event.id,
    Type: DocType.EVENT,
    TaskType: TaskType.personalEvent,
    SupplementaryGroupsIDs: [],
    UserID: LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
    OwnerID: LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
    PersonalOwnerId:
      event.organizer?.emailAddress?.address ??
      LocalStorage.get(LocalStorageKey.USER_ID) ??
      '',
    Status: EventStatus.ACTIVE,
    TimeZoneNameID: event.isAllDay ? 'UTC' : tz,
    LastUpdate: `${dayjs(event.lastModifiedDateTime).unix()}`,
    ThirdPartyLastUpdate: `${dayjs(event.lastModifiedDateTime).unix()}`,
    Rank: `${dayjs(event?.createdDateTime).unix()}`,
    Text: event.subject || 'No title',
    StartDate: start.toString(),
    EndDate: end.toString(),
    AllDay: event.isAllDay ? '1' : '0',
    Recurrence: (() => {
      const rruleString = (event as any).rruleString
      if (!rruleString) return undefined

      // Split the rruleString into lines (RRULE and EXDATE)
      const recurrenceLines = rruleString
        .split('\n')
        .filter((line: string) => line.trim())

      // Find the RRULE line and parse it
      const rruleLine = recurrenceLines.find((line: string) =>
        line.startsWith('RRULE')
      )
      if (!rruleLine) return undefined

      const parsedRRule = RRule.fromString(rruleLine)

      // Pass the full recurrence array so EXDATE lines can be parsed
      return convertRRuleToGroupcalRecurrence(
        recurrenceLines,
        start,
        undefined,
        parsedRRule
      )
    })(), //mapOutlookRecurrenceToGroupcal(event.recurrence),
    Notes: generateTfmNote(event),
    Reminder: reminders,
    ParentTaskID:
      event.seriesMasterId ?? ((event as any).occurrenceId ? event.id : ''),
    Location: mapOutlookLocationToGroupcal(event.locations?.[0]),
    canAddAttendees: event.isOrganizer ? '1' : '0',
    RequestConfirmation: !event.isOrganizer ? '1' : '0',
    ConferenceData: conferenceData,
    changeKey: event.changeKey,
    attendees: event.attendees?.map((attendee) => {
      return {
        email: attendee.emailAddress?.address ?? '',
        displayName: attendee.emailAddress?.name ?? '',
        self: attendee.emailAddress?.address === email,
        responseStatus:
          attendee.emailAddress?.address === email
            ? outlookAttendeeStatusToGeneral(event.responseStatus?.response)
            : outlookAttendeeStatusToGeneral(attendee.status?.response)
      }
    })
  }
}

function groupcalRecurrenceTypeToOutlook(type: string | undefined | null) {
  switch (type) {
    case '1':
      return 'daily'
    case '2':
      return 'weekly'
    case '3':
      return 'absoluteMonthly'
    case '4':
      return 'absoluteYearly'
    default:
      return undefined
  }
}

function generateTfmNote(
  event: Event
): import('types/api/changes').EventNote[] | undefined {
  if (event.bodyPreview && event.bodyPreview.length > 0) {
    return [
      {
        Note: event.bodyPreview,
        NoteID: dayjs().unix(),
        Status: 1,
        FilePath: 'null'
      }
    ]
  }
}

function mapOutlookLocationToGroupcal(
  location:
    | import('@microsoft/microsoft-graph-types').NullableOption<
        import('@microsoft/microsoft-graph-types').Location
      >
    | undefined
): import('types/api/changes').EventLocation | undefined {
  if (location?.displayName) {
    return {
      Address: location.address
        ? (() => {
            const addressParts = [
              location.address?.street,
              location.address?.city,
              location.address?.state,
              location.address?.postalCode,
              location.address?.countryOrRegion
            ]
              .filter(
                (part) =>
                  !!part &&
                  part.toString().trim() !== '' &&
                  part.toString().trim().toLowerCase() !==
                    location.displayName?.toLowerCase()
              )
              .map((part) => part?.toString().trim())

            return addressParts.length > 0
              ? `${location.displayName} (${addressParts.join(', ')})`
              : location.displayName
          })()
        : (location.displayName ?? ''),
      Lat: (location.coordinates?.latitude ?? 0).toString(),
      Long: (location.coordinates?.longitude ?? 0).toString(),
      FilteredLocation: 'null'
    }
  }

  return undefined
}

export async function tfmEventToOutlook(event: EventDoc): Promise<Event> {
  let recurrence: PatternedRecurrence | undefined = undefined
  let userSettings = await UserSettings()
  if (event.Recurrence && (event.Recurrence as EventRecurrence)) {
    recurrence = tfmRecurrenceToOutlookRecurrence(event, userSettings)
  }

  let tz = event.TimeZoneNameID

  let attendees: import('@microsoft/microsoft-graph-types').Attendee[] = []

  let responseStatus = undefined

  if (event.attendees) {
    attendees = event.attendees.map((attendee) => {
      const outlookAttendee = {
        emailAddress: {
          address: attendee.email,
          name: attendee.displayName
        },
        status: {
          response: generalStatusToOutlook(attendee.responseStatus),
          time: dayjs().toISOString()
        }
      }

      return outlookAttendee
    })

    responseStatus = {
      response: generalStatusToOutlook(
        event.attendees.find((attendee) => attendee.self)?.responseStatus
      ),
      time: dayjs().toISOString()
    }
  }
  Logger.blue('Outlook: Attendees', event._id)

  const outlookEvent: Event = {
    id: event._id,
    subject: event.Text,
    originalStartTimeZone: event.AllDay === '1' ? getDefaultTimezone() : tz,
    originalEndTimeZone: event.AllDay === '1' ? getDefaultTimezone() : tz,
    start: {
      dateTime: (event.AllDay === '1'
        ? dayjs.unix(Number(event.StartDate)).startOf('day')
        : dayjs(Number(event.StartDate) * 1000).tz(tz)
      ).format('YYYY-MM-DDTHH:mm:ss'),
      timeZone: event.AllDay === '1' ? getDefaultTimezone() : tz
    },
    end: {
      dateTime: (event.AllDay === '1'
        ? dayjs.unix(Number(event.EndDate)).startOf('day').add(1, 'day')
        : dayjs(Number(event.EndDate) * 1000).tz(tz)
      ).format('YYYY-MM-DDTHH:mm:ss'),
      timeZone: event.AllDay === '1' ? getDefaultTimezone() : tz
    },

    isAllDay: event.AllDay === '1',

    location: {
      displayName: event.Location?.Address,
      address: {
        street: event.Location?.Address
      },
      coordinates: {
        latitude: Number(event.Location?.Lat),
        longitude: Number(event.Location?.Long)
      }
    },
    responseStatus: responseStatus,
    attendees: attendees,
    body: event.Notes
      ? {
          content: event.Notes?.map((note) => note.Note).join('\n'),
          contentType: 'text'
        }
      : undefined,
    recurrence: recurrence,
    reminderMinutesBeforeStart: (event.Reminder?.[0].offset ?? 3600) / 60,
    isReminderOn: event.Reminder?.[0] !== undefined,
    isOnlineMeeting: attendees.length > 0
  }

  return outlookEvent
}

export function generalStatusToOutlook(
  responseStatus: string | undefined | null
):
  | import('microsoft-graph').NullableOption<
      import('microsoft-graph').ResponseType
    >
  | undefined {
  switch (responseStatus) {
    case 'accepted':
      return 'accepted'
    case 'declined':
      return 'declined'
    case 'tentative':
      return 'tentativelyAccepted'
    default:
      return 'none'
  }
}

export function outlookAttendeeStatusToGeneral(
  responseStatus?: import('microsoft-graph').ResponseType | null
): string {
  switch (responseStatus) {
    case 'accepted':
      return 'accepted'
    case 'declined':
      return 'declined'
    case 'tentativelyAccepted':
      return 'tentative'
    default:
      return 'needsAction'
  }
}

function isTokenExpired(token: string) {
  const base64Url = token.split('.')[1]
  if (base64Url === undefined) return true
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join('')
  )

  const { exp } = JSON.parse(jsonPayload)
  const currentTimestamp = Math.floor(new Date().getTime() / 1000)

  return currentTimestamp > exp
}

function tfmRecurrenceToOutlookRecurrence(
  event: EventDoc,
  userSettings: UserSettingsDoc
): PatternedRecurrence | undefined {
  const recur = event.Recurrence as EventRecurrence
  const type = groupcalRecurrenceTypeToOutlook(recur.Unit?.toString())
  const recurrence: PatternedRecurrence = {}

  const eventStartTime = dayjs.unix(Number(event.StartDate))
  const dayOfMonth = eventStartTime.date()
  const dayOfWeek = eventStartTime.day()
  let dayOfWeekOutlook: DayOfWeek = 'monday'

  // map dayjs day of week to outlook day of week, so 1 = 'monday' 2 = 'tuesday' etc

  switch (dayOfWeek) {
    case 1:
      dayOfWeekOutlook = 'monday'
      break
    case 2:
      dayOfWeekOutlook = 'tuesday'
      break
    case 3:
      dayOfWeekOutlook = 'wednesday'
      break
    case 4:
      dayOfWeekOutlook = 'thursday'
      break
    case 5:
      dayOfWeekOutlook = 'friday'
      break
    case 6:
      dayOfWeekOutlook = 'saturday'
      break
    case 0:
      dayOfWeekOutlook = 'sunday'
      break
  }

  const month = eventStartTime.month() + 1
  const days: DayOfWeek[] =
    recur.Unit === RECURRENCE_UNIT.yearly ||
    recur.Unit === RECURRENCE_UNIT.monthly
      ? []
      : recur.Unit === RECURRENCE_UNIT.weekly
        ? [dayOfWeekOutlook]
        : [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday'
          ]
  Logger.blue('Outlook: Days', days)
  recurrence.pattern = {
    type: type,
    month:
      recur.Unit === RECURRENCE_UNIT.yearly ||
      recur.Unit === RECURRENCE_UNIT.monthly
        ? month
        : 0,
    dayOfMonth:
      recur.Unit === RECURRENCE_UNIT.yearly ||
      recur.Unit === RECURRENCE_UNIT.monthly
        ? dayOfMonth
        : 0,
    firstDayOfWeek:
      userSettings.FirstDayOfTheWeek === 0
        ? 'sunday'
        : userSettings.FirstDayOfTheWeek === 1
          ? 'monday'
          : 'saturday',
    daysOfWeek: days,
    interval: Number(event.Recurrence?.Interval),
    index: 'first'
  }
  recurrence.range = {
    type: recur.EndDate ? 'endDate' : 'noEnd',
    startDate: dayjs.unix(Number(event.StartDate)).format('YYYY-MM-DD'),
    endDate: recur.EndDate
      ? dayjs.unix(Number(recur.EndDate)).format('YYYY-MM-DD')
      : undefined
  }

  return recurrence
}
